/*
 * Music: the user's picks by mood, played as a traditional game score. One
 * track at a time, silence between. A track, once started, plays to its
 * end: moods only choose the NEXT track. The one exception is combat, which
 * takes over after several seconds of steady fighting (a smoothed signal,
 * not a stray shot), holds at least a minute and a half, and eases out
 * only after a long calm. The files stream from the server (/music/...),
 * each loaded only when it plays. The volume settings apply every tick to
 * every element that may be sounding.
 */

import { settings } from "../ui/settings";

export type Mood = "general" | "industry-start" | "curious" | "bumpy" | "combat";

export interface Track {
  mood: Mood;
  file: string;
  title: string;
  artist: string;
  /** "CC BY 4.0", "CC BY 3.0" or "CC0". */
  license: string;
  source: string;
  /** Where the artist asks to be credited with a link. */
  artistUrl?: string;
}

/** What ships (deploy/music.tsv puts the files at these paths). */
export const TRACKS: Track[] = [
  { mood: "general", file: "general/airglow.ogg", title: "Airglow", artist: "Stellardrone", license: "CC BY 4.0", source: "https://stellardrone.bandcamp.com/album/light-years" },
  { mood: "general", file: "general/eternity.ogg", title: "Eternity", artist: "Stellardrone", license: "CC BY 4.0", source: "https://stellardrone.bandcamp.com/album/light-years" },
  { mood: "general", file: "general/light-years.ogg", title: "Light Years", artist: "Stellardrone", license: "CC BY 4.0", source: "https://stellardrone.bandcamp.com/album/light-years" },
  { mood: "general", file: "general/ultra-deep-field.ogg", title: "Ultra Deep Field", artist: "Stellardrone", license: "CC BY 4.0", source: "https://stellardrone.bandcamp.com/album/light-years" },
  { mood: "general", file: "general/dark-ambient-drone.ogg", title: "Dark Ambient Drone", artist: "Tsorthan Grove", license: "CC BY 4.0", source: "https://opengameart.org/content/dark-ambient-drone" },
  { mood: "industry-start", file: "industry-start/downfall.ogg", title: "Downfall", artist: "Vitalezzz", license: "CC0", source: "https://opengameart.org/content/downfall" },
  { mood: "curious", file: "curious/i-swear-i-saw-it.ogg", title: "I swear I saw it", artist: "yd", license: "CC0", source: "https://opengameart.org/content/i-swear-i-saw-it-background-track" },
  { mood: "curious", file: "curious/spacelife-14.ogg", title: "Spacelife #14", artist: "yd", license: "CC0", source: "https://opengameart.org/content/spacelife-14" },
  { mood: "bumpy", file: "bumpy/pondering-the-cosmos.ogg", title: "Pondering the Cosmos", artist: "Ruskerdax", license: "CC0", source: "https://opengameart.org/content/pondering-the-cosmos" },
  { mood: "bumpy", file: "bumpy/claimed-by-the-void.ogg", title: "Claimed by the Void", artist: "Vitalezzz", license: "CC0", source: "https://opengameart.org/content/claimed-by-the-void" },
  { mood: "combat", file: "combat/heart-of-machine.ogg", title: "Heart of Machine", artist: "Alexandr Zhelanov", license: "CC BY 3.0", source: "https://opengameart.org/content/heart-of-machine", artistUrl: "https://soundcloud.com/alexandr-zhelanov" },
  { mood: "combat", file: "combat/iron-wasteland.ogg", title: "Iron wasteland", artist: "Alexandr Zhelanov", license: "CC BY 4.0", source: "https://opengameart.org/content/iron-wasteland", artistUrl: "https://soundcloud.com/alexandr-zhelanov" },
];

// The local dev server has no music of its own: it plays the site's.
const BASE = /^(localhost|127\.)/.test(location.hostname) ? "https://ferro.henigin.dev/music/" : "/music/";

/** Seconds of silence between tracks (a random span). */
const GAP: [number, number] = [45, 150];
/** Fighting, smoothed: the signal follows over this many seconds; combat
 *  is on above IN (several seconds of steady fighting) and off below OUT. */
const FIGHT_TAU = 5;
const FIGHT_IN = 0.7;
const FIGHT_OUT = 0.15;
/** Combat music, once on, holds at least this long (seconds), and leaves
 *  only after this long calm. */
const COMBAT_HOLD = 90;
const COMBAT_CALM = 45;
/** Combat tracks in one episode at most: a big empire fights somewhere
 *  nearly always, and endless combat music wears; after these, the usual
 *  rotation, until the fighting stops and starts again. */
const COMBAT_RUN = 2;
/** A turbulent moment leans the next track bumpy at most this often. */
const BUMPY_EVERY = 600;

const now = () => performance.now();
/** Decisions to the console: ?musicdebug, or localStorage "ferro.debug.music". */
const DEBUG = (() => {
  try {
    return new URLSearchParams(location.search).has("musicdebug") || !!localStorage.getItem("ferro.debug.music");
  } catch {
    return false;
  }
})();
const log = (...a: unknown[]) => DEBUG && console.log(`[music ${new Date().toISOString().slice(11, 19)}]`, ...a);

/** The volume settings now: Master times Music, or nothing when Music is
 *  off. Read every tick, so a change applies at once to what's playing. */
const volume = () => (settings.musicOn === false ? 0 : Math.max(0, Math.min(1, (settings.master / 100) * (settings.music / 100))));

/** An element that may be sounding: its track, its fade level and where
 *  that's heading. */
interface Voice {
  el: HTMLAudioElement;
  track: Track;
  level: number;
  target: number;
  /** Fade speed, level per second. */
  rate: number;
}
/** The track playing, and every one fading out: each gets the volume every
 *  tick, and a faded one is stopped and dropped (none is left playing
 *  untracked). */
let current: Voice | null = null;
const outgoing = new Set<Voice>();
/** When the next track may start (ms), while silent. */
let nextAt = 0;
/** No track starts before this (ms): after one failed to load or wasn't
 *  allowed to play, so a failure can never become a rapid retry loop. */
let blockedUntil = 0;
let unlocked = false;
let started = false;
let lastFile = "";

// Signals from the game.
let fightRaw = false;
let fight = 0;
let inCombat = false;
let combatStarted = 0;
/** Combat tracks started since combat came on. */
let combatRun = 0;
let calmSince = 0;
let curiousUntil = 0;
let bumpyAt = -Infinity;
let bumpyNext = false;
let industryNext = false;
let industryDone = false;

function pick(mood: Mood): Track {
  const pool = TRACKS.filter((t) => t.mood === mood && t.file !== lastFile);
  const list = pool.length ? pool : TRACKS.filter((t) => t.mood === mood);
  return list[Math.floor(Math.random() * list.length)];
}

function retire(v: Voice, secs: number): void {
  v.target = 0;
  v.rate = 1 / Math.max(0.3, secs);
  outgoing.add(v);
}

function silence(gap: number): void {
  nextAt = now() + gap * 1000;
}

/** Starts `track`; the one playing, if any, fades out over `secs`. */
function play(track: Track, secs: number): void {
  log("play", track.mood, track.file, "after", current?.track.file ?? "silence");
  if (current) retire(current, secs);
  const el = new Audio(BASE + track.file);
  el.preload = "auto";
  el.volume = 0;
  const v: Voice = { el, track, level: 0, target: 1, rate: 1 / Math.max(0.3, secs) };
  el.addEventListener("ended", () => {
    outgoing.add(v);
    if (current !== v) return;
    log("ended", track.file);
    current = null;
    // After combat, a short breath; after anything else, a long silence.
    silence(track.mood === "combat" ? 15 + Math.random() * 20 : GAP[0] + Math.random() * (GAP[1] - GAP[0]));
  });
  el.addEventListener("error", () => {
    outgoing.add(v);
    if (current !== v) return;
    log("error", track.file);
    current = null;
    silence(60);
    blockedUntil = now() + 60_000;
  });
  void el.play().catch(() => {
    outgoing.add(v);
    if (current !== v) return;
    log("not allowed to play yet");
    current = null;
    silence(5);
    blockedUntil = now() + 5000;
  });
  current = v;
  lastFile = track.file;
}

function fadeOut(secs: number, gap: number, why: string): void {
  if (!current) return;
  log("fade out", current.track.file, why, "then silence", Math.round(gap), "s");
  retire(current, secs);
  current = null;
  silence(gap);
}

/** What to start when silence ends (combat is handled apart). */
function nextMood(t: number): Mood {
  if (industryNext && !industryDone) return "industry-start";
  if (bumpyNext) return "bumpy";
  if (t < curiousUntil) return "curious";
  return "general";
}

function step(dt: number): void {
  const t = now();
  const vol = volume();
  // Fades, and the volume settings, to everything that may be sounding.
  for (const v of [current, ...outgoing]) {
    if (!v) continue;
    v.level = v.target > v.level ? Math.min(v.target, v.level + v.rate * dt) : Math.max(v.target, v.level - v.rate * dt);
    v.el.volume = Math.max(0, Math.min(1, v.level * vol));
  }
  for (const v of outgoing) {
    if (v === current) continue;
    if (v.level <= 0 || v.el.ended || v.el.error) {
      v.el.pause();
      v.el.removeAttribute("src");
      v.el.load();
      outgoing.delete(v);
    }
  }
  // Fighting, smoothed, with separate thresholds in and out.
  fight += ((fightRaw ? 1 : 0) - fight) * Math.min(1, dt / FIGHT_TAU);
  if (!inCombat && fight >= FIGHT_IN) {
    inCombat = true;
    combatRun = 0;
    log("combat on");
  } else if (inCombat && fight <= FIGHT_OUT) {
    inCombat = false;
    calmSince = t;
    log("combat off");
  }
  // Music off: what plays fades out at once, and nothing starts.
  if (vol <= 0) {
    if (current) fadeOut(1, 8, "music off");
    return;
  }
  if (!unlocked) return;
  if (!started) {
    started = true;
    silence(8 + Math.random() * 12);
    log("unlocked: first track in a few seconds");
  }
  const mood = current?.track.mood;
  if (t < blockedUntil) return;
  const combatWanted = inCombat && combatRun < COMBAT_RUN;
  // Combat takes over a calm track (never restarts itself).
  if (combatWanted && current && mood !== "combat" && combatRun === 0) {
    combatStarted = t;
    combatRun++;
    play(pick("combat"), 2.5);
    return;
  }
  // Combat music leaves only after its hold and a long calm.
  if (mood === "combat" && !inCombat && t - combatStarted > COMBAT_HOLD * 1000 && t - calmSince > COMBAT_CALM * 1000) {
    fadeOut(6, 20 + Math.random() * 25, "fighting over");
    return;
  }
  // Silence: combat at once if fighting; else the next track when the gap
  // ends, by the moods leaning now.
  if (!current && ((combatWanted && combatRun === 0) || t >= nextAt)) {
    if (combatWanted) {
      combatStarted = t;
      combatRun++;
      play(pick("combat"), 2.5);
      return;
    }
    const m = nextMood(t);
    if (m === "industry-start") {
      industryDone = true;
      industryNext = false;
    }
    if (m === "bumpy") bumpyNext = false;
    play(pick(m), 4);
  }
}

// ---- What the game tells it (all of it only leans the NEXT track) ----

/** Whether the commander's units are fighting now (called every frame). */
export function musicFighting(on: boolean): void {
  if (on !== fightRaw) log("fighting", on);
  fightRaw = on;
}

/** Prospecting under way: the next track leans curious for a while. */
export function musicCurious(): void {
  if (now() > curiousUntil) log("curious leaning");
  curiousUntil = now() + 5 * 60_000;
}

/** A turbulent moment (a breach, a structure lost, a fresh start). */
export function musicBumpy(): void {
  const t = now();
  if (t - bumpyAt < BUMPY_EVERY * 1000) return;
  bumpyAt = t;
  bumpyNext = true;
  log("bumpy leaning");
}

/** The player's first industry going up (once per world and side). */
export function musicIndustryStart(key: string): void {
  if (industryDone || industryNext) return;
  try {
    if (localStorage.getItem(`ferro.music.industry.${key}`)) return;
    localStorage.setItem(`ferro.music.industry.${key}`, "1");
  } catch {
    /* once this session */
  }
  industryNext = true;
  log("industry start next");
  // In silence it needn't wait out the whole gap.
  if (!current) nextAt = Math.min(nextAt, now() + 8000);
}

// For debugging: what's sounding and how loud.
if (DEBUG) (window as unknown as { __music: unknown }).__music = () => ({ current: current && { file: current.track.file, volume: current.el.volume, level: current.level }, outgoing: [...outgoing].map((v) => ({ file: v.track.file, volume: v.el.volume })), fight, inCombat, combatRun, volume: volume() });

// ---- Start-up ----

// Browsers play only after a click or a key.
const unlock = () => {
  unlocked = true;
  window.removeEventListener("pointerdown", unlock, true);
  window.removeEventListener("keydown", unlock, true);
};
window.addEventListener("pointerdown", unlock, true);
window.addEventListener("keydown", unlock, true);

// One clock for the module, however often the game rebuilds.
let last = now();
window.setInterval(() => {
  const t = now();
  step(Math.min(1, (t - last) / 1000));
  last = t;
}, 100);
